import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import {
  projectOwnedBlob,
  type ShellSize,
  type WidgetAppConfig,
} from '@pypestream/pype/customization';

import { checkForErrors } from '../../utils/check-for-errors';

/**
 * Wire-format schema version for the persisted widget design blob. Sent on
 * every mutation so the backend can route forwards-incompatible payloads. We
 * don't currently version the JSON shape on the frontend — bump this only
 * when the structure of `WidgetAppConfig` changes in a way the backend cares
 * about.
 */
const SCHEMA_VERSION = 1;

/**
 * The wire shape, deliberately NOT `WidgetAppConfig`.
 *
 * Two keys were retired from the design contract — no field, no default, no
 * runtime read — but Engagement's validator still requires them, so the wire
 * carries constants. Keeping the divergence in a type here is what stops "the
 * backend needs it" from turning back into a designer-owned field.
 *
 * Both are deletable in one commit once the contract-alignment change is
 * deployed to live AND sandbox — the designer writes to both, so a single
 * upgraded environment is not enough.
 */
type PersistedWidgetAppConfig = Omit<WidgetAppConfig, 'branding' | 'shell'> & {
  branding: WidgetAppConfig['branding'] & { customerName: string };
  shell: Omit<WidgetAppConfig['shell'], 'size'> & {
    size?: ShellSize & { lockRatio: boolean };
  };
};

/**
 * Project the blob down to exactly what the platform owns, plus the two
 * backend-required keys above.
 *
 * The owned key set is DERIVED from `PLATFORM_FIELDS`, so a new blob field
 * persists by virtue of being declared there — this file used to restate the
 * field list, which meant a field could be added to the contract and silently
 * stop at the wire. The two constants cannot come from the registry: neither is
 * on `WidgetAppConfig` any more, only on what Engagement still demands.
 */
function toPersistedConfig(config: WidgetAppConfig): PersistedWidgetAppConfig {
  const owned = projectOwnedBlob(config);
  const { size } = owned.shell;
  return {
    ...owned,
    shell: {
      ...owned.shell,
      // Backend-required, designer-retired — see `PersistedWidgetAppConfig`.
      // `false` is the new semantic, not a preserved value: the ratio-lock
      // control is gone, so a blob that still reads `true` means "set before
      // the control was removed", and the deployed widget would honour it.
      size: size ? { ...size, lockRatio: false } : size,
    },
    branding: {
      ...owned.branding,
      // Backend-required, designer-retired — see `PersistedWidgetAppConfig`.
      // Never read from `branding`: a stored blob's stale value must not survive.
      customerName: '',
    },
  };
}

interface MutateArgs {
  appId: string;
  env: 'live' | 'sandbox';
  projectId: string;
  config: WidgetAppConfig;
}

/**
 * Persist a widget design config to the env-specific endpoint. Throws on any
 * GraphQL or network error so callers can `await mutateWidgetAppDesign(...)`
 * inside a `try/catch` and surface a snackbar on failure.
 */
export async function mutateWidgetAppDesign({
  appId,
  env,
  projectId,
  config,
}: MutateArgs): Promise<void> {
  const mutate =
    env === 'live'
      ? urqlGql.mutatePutWidgetAppDesign
      : urqlGql.mutatePutWidgetAppDesignSandbox;

  const res = await mutate({
    appId,
    projectId,
    config: toPersistedConfig(config),
    schemaVersion: SCHEMA_VERSION,
  });

  checkForErrors(res);
}
